import type {
  CreateSizeSetRequest,
  SizeSet,
  SizeSetSystem,
  SizeSetValue,
  SizeSetValueRequest,
  UpdateSizeSetRequest,
} from '@nucleus-commerce/api-client/admin';

import {
  EQUIVALENT_SYSTEMS,
  MAX_SIZES,
  type SizeRangeValues,
  type SizeRowInput,
  type SizeSetFormInput,
  type SizeSetFormValues,
} from '../schemas/size-set-form-schema';

const isSystem = (key: string): key is SizeSetSystem =>
  (EQUIVALENT_SYSTEMS as readonly string[]).includes(key);

export function emptySizeSetForm(): SizeSetFormInput {
  return { key: '', name: '', system: 'us', unit: '', sizeGuideHandle: '', values: [] };
}

/** What the grid does not edit: the starter sets carry measurements here (circumferenceMm, cm). */
function extraEquivalents(equivalents: SizeSetValue['equivalents']): Record<string, unknown> {
  return Object.fromEntries(Object.entries(equivalents ?? {}).filter(([key]) => !isSystem(key)));
}

/** Equivalents are edited per system as text; keys that name no system are not shown. */
function fromEquivalents(equivalents: SizeSetValue['equivalents']): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(equivalents ?? {})) {
    if (isSystem(key) && (typeof value === 'string' || typeof value === 'number')) {
      out[key] = String(value);
    }
  }
  return out;
}

export function fromSizeSet(row: SizeSet): SizeSetFormInput {
  return {
    key: row.key,
    name: row.name,
    system: row.system,
    unit: row.unit ?? '',
    sizeGuideHandle: row.sizeGuideHandle ?? '',
    values: [...row.values]
      .sort((a, b) => a.position - b.position)
      .map((value) => ({
        value: value.value,
        label: value.label,
        numericValue: value.numericValue === null ? '' : String(value.numericValue),
        equivalents: fromEquivalents(value.equivalents),
        extraEquivalents: extraEquivalents(value.equivalents),
      })),
  };
}

/** Systems that hold an equivalent in any row, in the canonical order: one grid column each. */
export function equivalentColumns(rows: ReadonlyArray<Pick<SizeRowInput, 'equivalents'>>) {
  const used = new Set(
    rows.flatMap((row) =>
      Object.entries(row.equivalents)
        .filter(([, value]) => value?.trim())
        .map(([key]) => key),
    ),
  );
  return EQUIVALENT_SYSTEMS.filter((system) => used.has(system));
}

/** The list sent on create and update; the order sent is the stored position. */
function toValues(values: SizeSetFormValues['values']): SizeSetValueRequest[] {
  return values.map((row) => {
    const equivalents = {
      ...row.extraEquivalents,
      ...Object.fromEntries(
        Object.entries(row.equivalents).filter(([key, value]) => isSystem(key) && value),
      ),
    };
    return {
      value: row.value,
      label: row.label,
      ...(row.numericValue === '' ? {} : { numericValue: Number(row.numericValue) }),
      ...(Object.keys(equivalents).length > 0 ? { equivalents } : {}),
    };
  });
}

export function toSizeSetCreate(values: SizeSetFormValues): CreateSizeSetRequest {
  return {
    key: values.key,
    name: values.name,
    system: values.system,
    ...(values.unit === '' ? {} : { unit: values.unit }),
    ...(values.sizeGuideHandle === '' ? {} : { sizeGuideHandle: values.sizeGuideHandle }),
    values: toValues(values.values),
  };
}

/**
 * Key never changes. `values` replaces the whole list server side, so it is sent only when the
 * sizes differ from the saved ones; a rename then leaves the stored sizes untouched. A blank
 * size guide handle clears it, so shoppers fall back to the store-wide guide.
 */
export function toSizeSetUpdate(
  values: SizeSetFormValues,
  existing: SizeSet,
): UpdateSizeSetRequest {
  const next = toValues(values.values);
  const saved = toValues(fromSizeSet(existing).values);
  return {
    name: values.name,
    system: values.system,
    unit: values.unit === '' ? null : values.unit,
    sizeGuideHandle: values.sizeGuideHandle === '' ? null : values.sizeGuideHandle,
    ...(JSON.stringify(next) === JSON.stringify(saved) ? {} : { values: next }),
  };
}

const decimals = (v: string) => v.split('.')[1]?.length ?? 0;

/**
 * Sizes from `from` to `to` by `step`, inclusive, as grid rows. Counted in whole units of the
 * finest decimal place given, so 3 to 13 by 0.25 ends on 13 exactly rather than 12.999….
 */
export function sizeRange({ from, to, step }: SizeRangeValues): SizeRowInput[] {
  const places = Math.max(decimals(from), decimals(to), decimals(step));
  const scale = 10 ** places;
  const start = Math.round(Number(from) * scale);
  const end = Math.round(Number(to) * scale);
  const stride = Math.round(Number(step) * scale);
  const rows: SizeRowInput[] = [];
  // Counted, not accumulated: past 2^53 adding the stride no longer moves the number.
  const count = Math.min(Math.floor((end - start) / stride), MAX_SIZES);
  for (let i = 0; i <= count; i += 1) {
    const text = String((start + i * stride) / scale);
    rows.push({ value: text, label: text, numericValue: text, equivalents: {} });
  }
  return rows;
}
